import {
	ActiveSharedBetSlip,
	ChatMessageType,
	ReplyMessageType,
} from "@data-types/chat";
import { IErrorMessage } from "@state/providers/diagnosticsProvider";

let sdkModule: typeof import("@amityco/ts-sdk") | null = null;

async function getSDK() {
	if (!sdkModule) {
		sdkModule = await import("@amityco/ts-sdk");
	}
	return sdkModule;
}

function getSDKSync() {
	if (!sdkModule) {
		throw new Error("Chat SDK not loaded. Initialize chat first.");
	}
	return sdkModule;
}

function isSDKLoaded() {
	return sdkModule !== null;
}

class ChatSDKService {
	/**
	 *
	 * @param errorCode undefined | number
	 * @returns {
	 * 		errorName: string;
	 * 		localizedErrorKey: string;
	 * 		blocking: boolean;
	 * }
	 */
	static getClientErrors(errorCode?: number): {
		errorName: string;
		isBlocking: boolean;
		localizedErrorKey: string;
	} | null {
		if (!errorCode) {
			return null;
		}

		let errorName;
		let isBlocking = false;
		let localizedErrorKey;
		switch (errorCode) {
			case 400000:
				//Request contains invalid parameters.
				errorName = "BadRequestError";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				isBlocking = true;
				break;
			case 400001:
				//An invalid regex rule retrieved from or added to the Blocklist
				// this error can happen if someone in admin dashboard ads an invalid regex rule,
				// user should not see an error message in this case
				errorName = null;
				localizedErrorKey = null;
				break;
			case 400002:
				//Video format not supported
				errorName = "VideoFormatInvalidRequestError";
				localizedErrorKey =
					"CHAT.ERR_HANDLING.VIDEO_FORMAT_NOT_SUPPORTED";
				break;
			case 400100:
				//Unverified user performs any action that requires access token verification.
				errorName = "UnauthorizedError";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				isBlocking = true;
				break;
			case 400300:
				//User performs forbidden action such as uploading a pdf file in an image message.
				errorName = "ForbiddenError";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				break;
			case 400301:
				//User has no permission to perform the action.
				errorName = "PermissionDenied";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				isBlocking = true;
				break;
			case 400302:
				//A muted user sends a message.
				errorName = "UserIsMuted";
				localizedErrorKey = "CHAT.ERR_HANDLING.USER_MUTED";
				break;
			case 400303:
				//User sends a message in a muted channel.
				errorName = "ChannelIsMuted";
				localizedErrorKey = "CHAT.ERR_HANDLING.CHANNEL_MUTED";
				break;
			case 400304:
				//User accessed a channel or community where he is banned.
				errorName = "UserIsBanned";
				localizedErrorKey =
					"CHAT.ERR_HANDLING.USER_BANNED_FROM_CHANNEL";
				isBlocking = true;
				break;
			case 400307:
				//User reached the limit of the number of sent messages containing blocklisted words.
				errorName = "MaxRepetitionExceed";
				localizedErrorKey =
					"CHAT.ERR_HANDLING.REACHED_BLOCKLIST_WORD_LIMIT";
				break;
			case 400308:
				//User sends a message that contains a blocklisted word.
				errorName = "BanWordFound";
				localizedErrorKey =
					"CHAT.ERR_HANDLING.MSG_INCLUDES_BLACKLIST_WORD";
				break;
			case 400309:
				//User sends a message that contains link that is not in the Allowlist.
				errorName = "LinkNotAllowed";
				localizedErrorKey = "CHAT.ERR_HANDLING.LINK_NOT_WHITELISTED";
				break;
			case 400311:
				//Web socket rate limit is exceeded.
				errorName = "RPCRateLimitError";
				localizedErrorKey = "CHAT.ERR_HANDLING.TOO_MANY_ACTIONS";
				break;
			case 400312:
				//Banned user performs any action.
				errorName = "GlobalBanError";
				localizedErrorKey = "CHAT.ERR_HANDLING.GLOBAL_BAN";
				isBlocking = true;
				break;
			case 400314:
				//Content moderation system detects unsafe content (eg. nudity).
				errorName = "UnsafeContent";
				localizedErrorKey = "CHAT.ERR_HANDLING.UNSAFE_CONTENT_DETECTED";
				break;
			case 400315:
				//Display name of a collection already exists (eg. for your channels or community categories).
				errorName = "DuplicateEntryError";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				break;
			case 400316:
				//Returned when unbanning a user that is not banned.
				errorName = "UserIsUnbanned";
				localizedErrorKey = "CHAT.ERR_HANDLING.UNBAN_ACTION_ERROR";
				break;
			case 400317:
				//The only active moderator in a channel/community attempts to leave and there are no other moderators in the group.
				errorName = "ForbiddenError";
				localizedErrorKey =
					"CHAT.ERR_HANDLING.LEAVE_CHANNEL_NOT_POSSIBLE";
				break;
			case 400318:
				//The only moderator in a channel/community attempts to leave and there are no other members in the group.
				errorName = "ForbiddenError";
				localizedErrorKey =
					"CHAT.ERR_HANDLING.LEAVE_CHANNEL_NOT_POSSIBLE";
				break;
			case 400319:
				//User changes module and user notification settings but the network notification setting is off.
				errorName = "ForbiddenError";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				isBlocking = true;
				break;
			case 400400:
				//System cannot find any resource matched with the requested condition.
				errorName = "ItemNotFound";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				isBlocking = true;
				break;
			case 400900:
				//System cannot create/update duplicated data.
				errorName = "Conflict";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				isBlocking = true;
				break;
			case 500000:
				//Uncategorized internal system errors not related to any user input./Video upload failed
				errorName = "ForbiddenError";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				isBlocking = false;
				break;
			case 800000:
				//Uncategorized errors.
				//To debug, refer to the 'error.message' property.
				errorName = "Unknown";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				break;
			case 800110:
				//Data type of the parameter is invalid.
				errorName = "InvalidParameter";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				break;
			case 800210:
				//Websocket connection of the SDK cannot reach the platform server. This could also be the case if a user is global-banned and try to register a session.
				errorName = "ConnectionError";
				localizedErrorKey = "CHAT.ERR_HANDLING.DEFAULT";
				isBlocking = true;
				break;
			default:
				errorName = null;
				localizedErrorKey = null;
		}

		if (!errorName || !localizedErrorKey) {
			//Not client error
			return null;
		}

		return {
			errorName,
			localizedErrorKey,
			isBlocking,
		};
	}

	static returnCurrentClient(): Amity.Client | null {
		if (!isSDKLoaded()) {
			return null;
		}
		try {
			//sdk throws an error if client does not exist
			return getSDKSync().Client.getActiveClient();
		} catch (e) {
			return null;
		}
	}

	static enableUnreadCount() {
		if (!isSDKLoaded()) {
			return;
		}
		const response = this.returnCurrentClient();

		if (!response?.token?.accessToken) {
			return;
		}

		getSDKSync().Client.startUnreadSync();
	}

	static disableUnreadCount() {
		if (!isSDKLoaded()) {
			return;
		}
		getSDKSync().Client.stopUnreadSync();
	}

	static async markAsRead(channelId: string) {
		const sdk = await getSDK();
		await sdk.ChannelRepository.markAsRead(channelId);
	}

	static async softDeleteMessage(messageId: string) {
		const sdk = await getSDK();
		const message = await sdk.MessageRepository.softDeleteMessage(messageId);

		return message;
	}

	static async createClientInstance(
		apiKey: string,
		apiRegion: string
	): Promise<Amity.Client> {
		const sdk = await getSDK();
		return sdk.Client.createClient(apiKey, apiRegion);
	}

	static async destroyClientInstance(): Promise<boolean> {
		const sdk = await getSDK();
		return await sdk.Client.logout();
	}

	static async loginUser(
		userId: string,
		authToken: string,
		displayName: string,
		sessionHandler: Amity.SessionHandler
	): Promise<boolean> {
		const sdk = await getSDK();
		return await sdk.Client.login(
			{ userId: userId, displayName: displayName, authToken: authToken },
			sessionHandler
		);
	}

	static logoutUser(): Promise<boolean> {
		if (!isSDKLoaded()) {
			return Promise.resolve(true);
		}
		return getSDKSync().Client.logout();
	}

	static getChannelInfo(
		channelId: string,
		callback: Amity.LiveObjectCallback<Amity.Channel>
	): Amity.Unsubscriber {
		return getSDKSync().ChannelRepository.getChannel(channelId, callback);
	}

	static async joinChannel(channelId: string): Promise<boolean> {
		const sdk = await getSDK();
		return await sdk.ChannelRepository.joinChannel(channelId);
	}

	async createChannel(): Promise<any> {
		const sdk = await getSDK();
		return await sdk.ChannelRepository.createChannel({
			type: "community",
			displayName: "user-chat-support",
			isPublic: false,
		});
	}

	static async leaveChannel(channelId: string): Promise<boolean> {
		const sdk = await getSDK();
		return await sdk.ChannelRepository.leaveChannel(channelId);
	}

	static subscribeToNewMessages(
		subChannelId: string,
		callback: Amity.LiveCollectionCallback<Amity.Message>,
		msgLimit: number = 30
	): Amity.Unsubscriber {
		return getSDKSync().MessageRepository.getMessages(
			{
				subChannelId: subChannelId,
				limit: msgLimit,
			},
			callback
		);
	}

	static async createMessage(params: {
		subChannelId: string;
		message: string;
		isLink?: boolean;
		isMultiOffer?: boolean;
		file?: File;
		diagnosticsData?: IErrorMessage | null;
		messageFeedId?: string;
	}): Promise<Amity.Cached<Amity.Message>> {
		const sdk = await getSDK();
		const {
			message,
			subChannelId,
			diagnosticsData,
			file,
			isLink,
			isMultiOffer,
			messageFeedId,
		} = params;

		let messageData = {
			subChannelId,
			messageFeedId: messageFeedId || subChannelId,
		};

		if (file) {
			const isImage = file.type.includes("image");
			const data = new FormData();
			data.append("files", file);

			const { data: files } = isImage
				? await sdk.FileRepository.uploadImage(data)
				: await sdk.FileRepository.uploadFile(data);

			Object.assign(messageData, {
				data: {
					caption: message,
					fileId: files[0].fileId,
				},
				fileId: files[0].fileId,
				dataType: isImage
					? sdk.MessageContentType.IMAGE
					: sdk.MessageContentType.FILE,
			});
		} else if (diagnosticsData) {
			Object.assign(messageData, {
				dataType: sdk.MessageContentType.CUSTOM,
				data: {
					text: message,
					errorData: JSON.stringify(diagnosticsData),
				},
			});
		} else {
			Object.assign(messageData, {
				dataType: sdk.MessageContentType.TEXT,
				data: {
					text: message,
				},
				metadata: {
					isLink: isLink,
					isMultiOffer: isMultiOffer,
				},
			});
		}

		return await sdk.MessageRepository.createMessage(messageData);
	}

	static getImageThumbnail(
		fileId: string,
		size: "small" | "medium" | "large" | "full"
	) {
		const thumbnail = getSDKSync().FileRepository.fileUrlWithSize(
			fileId,
			size
		);

		return thumbnail;
	}

	static observeFile(
		fileId: string,
		callback: (snapshot: Amity.Snapshot<Amity.File>) => void
	) {
		return getSDKSync().FileRepository.observeFile(fileId, callback);
	}

	static async createCustomMessage(
		subChannelId: string,
		sharedSlip: ActiveSharedBetSlip,
		text: string,
		isLink?: boolean,
		isMultiOffer?: boolean
	) {
		const sdk = await getSDK();
		const customMessage = {
			subChannelId: subChannelId,
			dataType: sdk.MessageContentType.CUSTOM,
			data: {
				text: text,
				sharedSlip: sharedSlip,
			},
			metadata: {
				isLink: isLink,
				isMultiOffer: isMultiOffer,
			},
		};

		const { data: message } =
			await sdk.MessageRepository.createMessage(customMessage);

		return message;
	}

	static async replyToMessage(
		subChannelId: string,
		repliyingMessage: ReplyMessageType,
		betSlip?: ActiveSharedBetSlip
	) {
		const sdk = await getSDK();
		const replyMessage = {
			subChannelId: subChannelId,
			parentId: repliyingMessage.messageId,
			dataType: sdk.MessageContentType.TEXT,
			data: {
				text: repliyingMessage.text,
				sharedSlip: repliyingMessage.sharedSlip, //old bet slip data
				replyingCreatorId: repliyingMessage.replyingCreatorId,
				replyingMessage: repliyingMessage.replyingMessage,
				messageType: ChatMessageType.REPLY,
				betSlipData: betSlip, //new bet slip data
			},
			metadata: {
				isLink: repliyingMessage.isLink,
				isMultiOffer: repliyingMessage.isMultiOffer,
			},
		};

		const { data: message } =
			await sdk.MessageRepository.createMessage(replyMessage);
		return message;
	}

	static createSessionHandler(authToken: string): Amity.SessionHandler {
		const sessionHandler: Amity.SessionHandler = {
			sessionWillRenewAccessToken(renewal: Amity.AccessTokenRenewal) {
				try {
					renewal.renewWithAuthToken(authToken);
				} catch (err) {
					renewal.unableToRetrieveAuthToken();
				}
			},
		};

		return sessionHandler;
	}

	static async flagMessage(messageId: string) {
		const sdk = await getSDK();
		const isFlagged = await sdk.MessageRepository.flagMessage(messageId);
		return isFlagged;
	}

	static async unFlagMessage(messageId: string) {
		const sdk = await getSDK();
		const isUnFlagged = await sdk.MessageRepository.unflagMessage(messageId);
		return isUnFlagged;
	}

	static async isMessageFlaggedByMe(messageId: string) {
		const sdk = await getSDK();
		const isFlaggedByMe =
			await sdk.MessageRepository.isMessageFlaggedByMe(messageId);
		return isFlaggedByMe;
	}
}

export default ChatSDKService;
